import { Button } from "@/components/ui/baseUi/button"
import { Input } from "@/components/ui/baseUi/input"
import { SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/baseUi/select"
import { Text } from "@/components/ui/baseUi/text"
import { withForm } from "@/components/ui/forms/hooks/useFormHook"
import { getRecurringPeriodInputLabel, getRecurringPeriodShortLabel } from "@/modules/boxOffer/utils/boxOfferUtils"
import { currencySymbol, formatCurrency } from "@/modules/currency/currency"
import { capitalize } from "@/modules/utils/stringUtils"
import { MoneyIcon } from "@images/svgIcons/moneyIcon"
import { NotepadIcon } from "@images/svgIcons/notepadIcon"
import { Ticket } from "lucide-react"
import { useCurrentBox } from "@modules/box/hooks/useBox"
import { type BoxOfferFormPageProps } from "@modules/boxOffer/components/boxOfferForm"
import {
  BoxOfferFormAlertCard,
  BoxOfferFormSection,
  BoxOfferInputCustomLabel,
} from "@modules/boxOffer/components/boxOfferFormSection"
import {
  BOX_OFFER_TICKET_BOOKS_MAX,
  BOX_OFFER_TICKET_BOOKS_MIN,
  BOX_OFFER_VALIDITY_MAX,
  BOX_OFFER_VALIDITY_MIN,
  BoxOfferPriceType,
  BoxOfferType,
  BoxOfferValidity,
  RecurringPeriod,
} from "@modules/boxOffer/entities/boxOfferTypes"
import { useBoxOfferSidebar } from "@modules/boxOffer/hooks/useBoxOfferSidebar"
import { boxOfferFormDefaultValues } from "@modules/boxOffer/types/boxOfferFormTypes"
import { word } from "@modules/core/utils/i18n"
import { FinanceFeesAlertCard } from "@modules/finances/components/financeFeesAlertCard"
import { useLanguage } from "@modules/language/hooks/useLanguage"
import { StripeAccountStatus } from "@modules/payment/stripeTypes"
import { useStore } from "@tanstack/react-form"
import { ArrowRight, CalendarIcon } from "lucide-react"
import { type SyntheticEvent, useEffect, useMemo } from "react"

const preventNonNumericInput = (e: SyntheticEvent<HTMLInputElement>) => {
  if ("data" in e && isNaN(Number(e.data))) {
    e.preventDefault()
  }
}

export const BoxOfferFormFirstPage = withForm({
  defaultValues: boxOfferFormDefaultValues,
  props: {} as BoxOfferFormPageProps,
  render: function BoxOfferFormFirstPageInner({ form, handleSubmit, isEdition }) {
    const language = useLanguage()
    const { result: box } = useCurrentBox()
    const { hideSidebar } = useBoxOfferSidebar()

    const type = useStore(form.baseStore, (state) => state.values.type)
    const priceSwitch = useStore(form.baseStore, (state) => state.values.priceSwitch)
    const recurringPeriod = useStore(form.baseStore, (state) => state.values.recurringPeriod)
    const validitySwitch = useStore(form.baseStore, (state) => state.values.validitySwitch)

    const priceOptions = [
      // Always put the free option first, it's the default value if the user is on a lite version
      { value: BoxOfferPriceType.Free, label: word("offer.form.price_switch.free") },
      { value: BoxOfferPriceType.Paid, label: word("offer.form.price_switch.price") },
    ]

    const validityOptions = Object.values(BoxOfferValidity).map((val) => ({
      label: word(`box.offer.form.infos.validity.types.${val}`),
      value: val,
    }))

    const recurringPeriodOptions = Object.values(RecurringPeriod).map((periodOption) => ({
      value: periodOption,
      label: getRecurringPeriodInputLabel(periodOption),
    }))

    const isBoxOfferLiteVersion = useMemo(
      () =>
        box &&
        (box.stripeAccountStatus === StripeAccountStatus.REQUIRE_ADDITIONAL_INFORMATION ||
          box.stripeAccountStatus === StripeAccountStatus.ONBOARDING ||
          box.stripeAccountStatus === StripeAccountStatus.UNDEFINED)
          ? true
          : false,
      [box],
    )

    // Set the price switch to free if the box offer is a lite version, it's necessary to set the drop-in to free
    useEffect(() => {
      if (isBoxOfferLiteVersion) form.setFieldValue("priceSwitch", BoxOfferPriceType.Free)
    }, [isBoxOfferLiteVersion, form])

    const displayPrice =
      ![BoxOfferType.Trial, BoxOfferType.DropIn, BoxOfferType.Millenium].includes(type) ||
      ([BoxOfferType.Trial, BoxOfferType.DropIn].includes(type) && priceSwitch === BoxOfferPriceType.Paid)

    const priceLabel =
      type === BoxOfferType.DropIn || type === BoxOfferType.Trial
        ? word(`offer.form.${type}.price.label`)
        : word("offer.form.price.label")

    const isMilleniumOrDropInLiteVersion =
      type === BoxOfferType.Millenium ||
      (type === BoxOfferType.DropIn && isBoxOfferLiteVersion) ||
      (type === BoxOfferType.Trial && isBoxOfferLiteVersion)

    return (
      <div className="flex flex-col gap-8 px-5 pt-20 pb-4">
        <div className="flex flex-col gap-2">
          <Text fontStyle="TL">{`${isEdition ? word(`offer.form.${type}.edit`) : word(`offer.form.${type}.create`)} (1/3)`}</Text>
          <Text fontStyle="BM" className="text-neutral-700">
            {word(`offer.form.${type}.subtitle`)}
          </Text>
        </div>

        <div className="flex flex-col gap-5">
          <BoxOfferFormSection
            title={word("box.offer.form.infos.generalInformation")}
            icon={<NotepadIcon color="currentColor" />}
            className="mb-5"
          />

          <form.AppField name="name">
            {(field) => (
              <field.Field>
                <field.Label required>{word("offer.form.title.label")}</field.Label>
                <field.Content>
                  <field.Input placeholder={word("offer.form.title.placeholder")} />
                  <field.Message />
                </field.Content>
              </field.Field>
            )}
          </form.AppField>

          <form.AppField name="description">
            {(field) => (
              <field.Field>
                <field.Label required>{word("offer.form.description.label")}</field.Label>
                <field.Content>
                  <field.Textarea
                    placeholder={word("offer.form.description.placeholder")}
                    rows={4}
                    className="field-sizing-fixed"
                  />
                  <field.Message />
                </field.Content>
              </field.Field>
            )}
          </form.AppField>
        </div>

        {type === BoxOfferType.TicketsBook && (
          <div className="flex flex-col gap-2">
            <BoxOfferFormSection
              title={word("box.offer.form.quantity")}
              icon={<Ticket color="currentColor" size={24} />}
              description={word("offer.form.numberOfSessions.TICKETS_BOOK.description")}
            />

            <form.AppField name="count">
              {(field) => (
                <field.Field>
                  <field.Label required htmlFor={field.name}>
                    {word("offer.form.numberOfSessions.title")}
                  </field.Label>
                  <field.Content>
                    <Input
                      id={field.name}
                      name={field.name}
                      className="w-full"
                      type="number"
                      inputMode="numeric"
                      min={BOX_OFFER_TICKET_BOOKS_MIN}
                      max={BOX_OFFER_TICKET_BOOKS_MAX}
                      step={1}
                      value={field.state.value ?? ""}
                      onBlur={field.handleBlur}
                      onChange={(e) => {
                        const parsed = parseInt(e.target.value)
                        field.handleChange(isNaN(parsed) ? undefined : parsed)
                      }}
                      onBeforeInput={preventNonNumericInput}
                      aria-invalid={field.state.meta.isTouched && !field.state.meta.isValid}
                    />
                    <field.Message />
                  </field.Content>
                </field.Field>
              )}
            </form.AppField>
          </div>
        )}

        <div className="flex flex-col gap-5">
          {!isBoxOfferLiteVersion && (
            <BoxOfferFormSection
              title={word("box.offer.form.price.title")}
              icon={<MoneyIcon color="currentColor" />}
              description={type !== BoxOfferType.Millenium ? word(`box.offer.form.price.${type}.subtitle`) : undefined}
            />
          )}

          {isMilleniumOrDropInLiteVersion && (
            <div className="flex flex-col gap-5">
              <div className="flex flex-col gap-2">
                <BoxOfferInputCustomLabel label={word("box.offer.form.price.title")} margin="" />
                <BoxOfferFormAlertCard
                  title={word("box.offer.form.alert.free.title")}
                  description={
                    isBoxOfferLiteVersion
                      ? word("box.offer.form.alert.free.liteVersion")
                      : word("box.offer.form.alert.free.description")
                  }
                />
              </div>
              {type === BoxOfferType.Millenium && (
                <div className="flex flex-col gap-2">
                  <BoxOfferInputCustomLabel label={word("box.offer.form.minimum_monthly_duration.label")} margin="" />
                  <BoxOfferFormAlertCard
                    title={word("box.offer.form.alert.commitment.title")}
                    description={word("box.offer.form.alert.commitment.description")}
                  />
                </div>
              )}
            </div>
          )}
          {[BoxOfferType.Trial, BoxOfferType.DropIn].includes(type) && (
            <form.AppField name="priceSwitch">
              {(field) => (
                <field.Field>
                  <field.Label>{word(`offer.form.TRIAL.price.label`)}</field.Label>
                  <field.Content>
                    <field.Select items={priceOptions} disabled={isBoxOfferLiteVersion}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {priceOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </field.Select>
                    <field.Message />
                  </field.Content>
                </field.Field>
              )}
            </form.AppField>
          )}

          {displayPrice && !isBoxOfferLiteVersion && (
            <>
              {type === BoxOfferType.Subscription && (
                <form.AppField name="recurringPeriod">
                  {(field) => (
                    <field.Field>
                      <field.Content>
                        <field.Select items={recurringPeriodOptions}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {recurringPeriodOptions.map((option) => (
                              <SelectItem key={option.value} value={option.value}>
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </field.Select>
                        <field.Message />
                      </field.Content>
                    </field.Field>
                  )}
                </form.AppField>
              )}

              <form.AppField name="price">
                {(field) => (
                  <field.Field>
                    <field.Label required htmlFor={field.name}>
                      {priceLabel}
                    </field.Label>
                    <field.Content>
                      <div className="flex flex-row items-center gap-2">
                        <field.PriceInput
                          locale={language}
                          inputProps={{
                            disabled: isBoxOfferLiteVersion,
                            placeholder: word("offer.form.price.placeholder", {
                              minPrice: formatCurrency(
                                box?.stripeMinimumAmount ?? 0,
                                box?.stripeAccountCurrency,
                                language,
                              ),
                            }),
                          }}
                        />
                        <Text fontStyle="TM" className="mr-2 whitespace-nowrap">
                          {currencySymbol(box?.stripeAccountCurrency ?? "", language)}{" "}
                          {type === BoxOfferType.Subscription ? getRecurringPeriodShortLabel(recurringPeriod) : null}
                        </Text>
                      </div>
                      <field.Message />
                    </field.Content>
                  </field.Field>
                )}
              </form.AppField>

              <FinanceFeesAlertCard />
            </>
          )}

          {type === BoxOfferType.Subscription && (
            <div className="flex flex-col gap-5">
              <form.AppField name="minimumMonthlyDuration">
                {(field) => (
                  <field.Field>
                    <field.Label required htmlFor={field.name}>
                      {word("box.offer.form.minimum_monthly_duration.label")}
                    </field.Label>
                    <field.Content>
                      <div className="flex flex-row items-center gap-2">
                        <Input
                          id={field.name}
                          name={field.name}
                          className="w-full"
                          type="number"
                          inputMode="numeric"
                          min={1}
                          max={24}
                          step={1}
                          placeholder={word("offer.form.minimumMonthlyDuration.placeholder")}
                          value={field.state.value ?? ""}
                          onBlur={field.handleBlur}
                          onChange={(e) => {
                            const parsed = parseInt(e.target.value)
                            if (isNaN(parsed) || parsed < 1) {
                              field.handleChange(1)
                            } else if (parsed > 24) {
                              field.handleChange(24)
                            } else {
                              field.handleChange(parsed)
                            }
                          }}
                          onBeforeInput={preventNonNumericInput}
                          aria-invalid={field.state.meta.isTouched && !field.state.meta.isValid}
                        />
                        <Text fontStyle="TM">
                          {capitalize(word("global.month.label", { count: field.state.value ?? 1 }))}
                        </Text>
                      </div>
                      <field.Message />
                    </field.Content>
                  </field.Field>
                )}
              </form.AppField>

              <BoxOfferFormAlertCard
                title={word("offer.form.subscription.alert_title")}
                description={word("offer.form.subscription.alert_description")}
              />

              <form.AppField name="commitmentPeriodAutoRenewal">
                {(field) => (
                  <field.Field>
                    <field.Content>
                      <field.SwitchInline
                        textSwitch={word("box.offer.form.infos.commitmentPeriodAutoRenewal.textSwitch")}
                      />
                      <Text fontStyle="BS" className="px-5 text-justify leading-5 text-textDefault-caption">
                        {word("box.offer.form.infos.commitmentPeriodAutoRenewal.description")}
                      </Text>
                      <field.Message />
                    </field.Content>
                  </field.Field>
                )}
              </form.AppField>
            </div>
          )}
        </div>

        {(type === BoxOfferType.Trial || type === BoxOfferType.TicketsBook || type === BoxOfferType.DropIn) && (
          <div className="flex flex-col gap-5">
            <BoxOfferFormSection
              title={word("box.offer.form.validity.title")}
              icon={<CalendarIcon color="currentColor" />}
              description={word(`box.offer.form.validity.${type}.description`)}
            />

            <form.AppField
              name="validitySwitch"
              listeners={{
                onChange: ({ value }) => {
                  if (value === BoxOfferValidity.UNLIMITED) {
                    form.setFieldValue("validityPeriodMonths", undefined)
                  }
                },
              }}
            >
              {(field) => (
                <field.Field>
                  <field.Label>{word("box.offer.form.validity.title")}</field.Label>
                  <field.Content>
                    <field.Select items={validityOptions}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {validityOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </field.Select>
                    <field.Message />
                  </field.Content>
                </field.Field>
              )}
            </form.AppField>

            {validitySwitch === BoxOfferValidity.DEFINED && (
              <form.AppField name="validityPeriodMonths">
                {(field) => (
                  <field.Field>
                    <field.Content>
                      <div className="flex flex-row items-center gap-2">
                        <Input
                          id={field.name}
                          name={field.name}
                          className="w-full"
                          type="number"
                          inputMode="numeric"
                          min={BOX_OFFER_VALIDITY_MIN}
                          max={BOX_OFFER_VALIDITY_MAX}
                          step={1}
                          placeholder={word("offer.form.minimumMonthlyDuration.placeholder")}
                          value={field.state.value ?? ""}
                          onBlur={field.handleBlur}
                          onChange={(e) => {
                            const parsed = parseInt(e.target.value)
                            field.handleChange(isNaN(parsed) ? undefined : parsed)
                          }}
                          onBeforeInput={preventNonNumericInput}
                          aria-invalid={field.state.meta.isTouched && !field.state.meta.isValid}
                        />
                        <Text fontStyle="TM">
                          {capitalize(word("global.month.label", { count: field.state.value ?? 1 }))}
                        </Text>
                      </div>
                      <field.Message />
                    </field.Content>
                  </field.Field>
                )}
              </form.AppField>
            )}
          </div>
        )}

        <div className="mt-5 mb-4 flex gap-5">
          <Button
            variant="outline"
            size="lg"
            className="grow px-3 py-2"
            onClick={() => {
              hideSidebar()
            }}
          >
            {word("global.cancel")}
          </Button>
          <Button variant="primary" size="lg" className="grow px-3 py-2" onClick={handleSubmit}>
            {word("global.next")}
            <ArrowRight size={18} color="currentColor" />
          </Button>
        </div>
      </div>
    )
  },
})
